"use client"

import type { ComponentProps } from "react"
import dynamic from "next/dynamic"
import Link from "next/link"
import { useAuth } from "@clerk/nextjs"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const CREATE_HREF = "/create"

const SignInButton = dynamic(
  () => import("@clerk/nextjs").then((m) => m.SignInButton),
  {
    loading: () => (
      <div
        className="flex h-11 items-center justify-center"
        aria-busy
        aria-label="Loading"
      >
        <span className="h-11 min-w-[10rem] animate-pulse rounded-full bg-muted/80" />
      </div>
    ),
  },
)

type MarketingCreateEventCtaProps = {
  className?: string
  size?: ComponentProps<typeof Button>["size"]
  label?: string
  variant?: ComponentProps<typeof Button>["variant"]
}

export function MarketingCreateEventCta({
  className,
  size = "lg",
  label = "Create your first event",
  variant = "inverse",
}: MarketingCreateEventCtaProps) {
  const { isSignedIn, isLoaded } = useAuth()
  const sharedClass = cn(
    "h-11 rounded-full px-8 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:opacity-95",
    className,
  )

  if (!isLoaded) {
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        className={sharedClass}
        disabled
        aria-busy
      >
        {label}
      </Button>
    )
  }

  if (isSignedIn) {
    return (
      <Button variant={variant} size={size} className={sharedClass} asChild>
        <Link href={CREATE_HREF}>{label}</Link>
      </Button>
    )
  }

  return (
    <SignInButton
      mode="modal"
      forceRedirectUrl={CREATE_HREF}
      signUpForceRedirectUrl={CREATE_HREF}
    >
      <Button type="button" variant={variant} size={size} className={sharedClass}>
        {label}
      </Button>
    </SignInButton>
  )
}
